import type { Priority } from "@app/core";
import { cn, Flag } from "@app/ui";
import type { I18nKey } from "../../../../lib/language.js";
import { motion, springs, useReducedMotion } from "../../../../lib/motion/index.js";
import { useI18n } from "../../../../lib/useI18n.js";
import { PRIORITY_FLAG_HEX } from "../../../task-colors.js";

export interface PriorityPick {
  color: string;
  rect: DOMRect;
}

interface Props {
  value: Priority;
  onChange: (priority: Priority) => void;
  /** Fired when a priority is newly SELECTED (drives the border sweep). */
  onPick?: (pick: PriorityPick) => void;
  /** Matrix system governs priority: read-only reflection of the derived flag. */
  readOnly?: boolean;
}

// "none" is the implicit default (no flag chosen) — it isn't offered as a button.
const PRIORITIES: Array<{ id: Exclude<Priority, "none">; labelKey: I18nKey }> = [
  { id: "low", labelKey: "sidebar.priority.low" },
  { id: "medium", labelKey: "sidebar.priority.medium" },
  { id: "high", labelKey: "sidebar.priority.high" },
];

/** Mobile priority picker. This directly previews the mobile block rule:
 *  priority becomes a colored border/glow in the final calendar block. Tapping
 *  the active flag again clears back to "none". */
export function PriorityField({ value, onChange, onPick, readOnly = false }: Props) {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const selectedIndex = PRIORITIES.findIndex((p) => p.id === value);
  const thumbLeft =
    selectedIndex === 0 ? "2px"
      : selectedIndex === 1 ? "calc(2px + (100% - 4px) / 3)"
        : "calc(2px + (100% - 4px) * 2 / 3)";
  return (
    <div className="relative flex w-[13.25rem] rounded-md bg-muted/70 p-0.5 text-xs font-medium" role="radiogroup" aria-label={t("sidebar.priority")}>
      {selectedIndex >= 0 && (
        <motion.span
          aria-hidden
          className="absolute inset-y-0.5 rounded bg-background shadow-sm"
          style={{ width: "calc((100% - 4px) / 3)" }}
          animate={{ left: thumbLeft }}
          transition={reduce ? { duration: 0 } : springs.snappy}
        />
      )}
      {PRIORITIES.map((p) => {
        const selected = value === p.id;
        const color = PRIORITY_FLAG_HEX[p.id];
        return (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-disabled={readOnly || undefined}
            title={readOnly ? t("matrix.priorityFromMatrix") : undefined}
            onClick={readOnly ? undefined : (e) => {
              if (selected) { onChange("none"); return; }
              onChange(p.id);
              onPick?.({ color, rect: e.currentTarget.getBoundingClientRect() });
            }}
            className={cn(
              "relative z-10 flex flex-1 items-center justify-center gap-1 rounded py-1 text-center transition-colors",
              selected ? "text-foreground" : "text-muted-foreground",
              readOnly && "cursor-default",
            )}
          >
            <Flag className="h-3 w-3" fill={color} stroke="currentColor" strokeWidth={2} />
            {t(p.labelKey)}
          </button>
        );
      })}
    </div>
  );
}
